import { useEffect, useState } from "react";
import { apiClient, ApiError, NotAuthenticatedError } from "../api/client.js";
import type { DaemonMachine } from "../api/types.js";
import { ConfirmDialog } from "./confirm-dialog.js";

// UM.M — käyttäjälle näkyvä koneiden hallinta /asetukset-sivulla. Listaa
// kayttajan omat (tai admin: kaikki view-scope=all:in kanssa) koneet
// last-seen-tilan + diski/source-root-määrän kanssa. "Poista"-nappi
// removaa machinen + cascade (daemon_disk, daemon_inbox). Asset:t säilyvät.

const STATUS_LABEL: Record<DaemonMachine["status"], string> = {
  online: "Online",
  unknown: "Vanhentumassa",
  offline: "Offline",
};

const STATUS_COLOR: Record<DaemonMachine["status"], string> = {
  online: "bg-emerald-900/40 text-emerald-300 border-emerald-700",
  unknown: "bg-amber-900/40 text-amber-300 border-amber-700",
  offline: "bg-slate-900/40 text-slate-400 border-slate-700",
};

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s sitten`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min sitten`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h sitten`;
  const d = Math.floor(h / 24);
  return `${d} pv sitten`;
}

export function MachinesSection() {
  const [machines, setMachines] = useState<DaemonMachine[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteCandidate, setDeleteCandidate] = useState<DaemonMachine | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    apiClient
      .listMachines()
      .then((res) => setMachines(res.machines))
      .catch((err: unknown) => {
        if (err instanceof NotAuthenticatedError) setError("Ei kirjautunut sisaan.");
        else if (err instanceof ApiError) setError(err.message);
        else setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  async function confirmDelete() {
    if (!deleteCandidate) return;
    setDeleting(true);
    try {
      await apiClient.deleteMachine(deleteCandidate.id);
      setMachines((prev) => prev.filter((m) => m.id !== deleteCandidate.id));
      setDeleteCandidate(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section className="rounded-lg border border-slate-700 bg-slate-900 p-6">
      <header className="mb-4 flex items-baseline justify-between gap-3">
        <h3 className="text-lg font-medium text-slate-100">Koneet</h3>
        <button
          type="button"
          onClick={load}
          className="text-xs text-cyan-400 hover:underline"
        >
          Päivitä
        </button>
      </header>

      {error && <p className="text-sm text-red-400">{error}</p>}
      {loading && machines.length === 0 && (
        <p className="text-sm text-slate-500">Ladataan…</p>
      )}
      {!loading && machines.length === 0 && (
        <p className="text-sm text-slate-500">
          Ei rekisteröityjä koneita. Asenna Kratti.app ja kirjaudu daemoniin
          aktivoidaksesi tilin.
        </p>
      )}

      {machines.length > 0 && (
        <ul className="divide-y divide-slate-800">
          {machines.map((m) => (
            <li
              key={m.id}
              className="flex flex-wrap items-center justify-between gap-3 py-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-100">
                    {m.label || m.hostname || "(nimetön)"}
                  </span>
                  <span
                    className={
                      "rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide " +
                      STATUS_COLOR[m.status]
                    }
                  >
                    {STATUS_LABEL[m.status]}
                  </span>
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {m.osPlatform ?? "?"}
                  {m.osVersion ? ` ${m.osVersion}` : ""}
                  {m.agentVersion ? ` · agent ${m.agentVersion}` : ""}
                  {" · "}
                  {m.sourceRootsCount} source-root
                  {m.sourceRootsCount === 1 ? "" : "ia"}
                  {" · "}
                  {m.disksCount} levy
                  {m.disksCount === 1 ? "" : "ä"} (10 min)
                </div>
                <div className="mt-0.5 text-[11px] text-slate-600">
                  Viimeksi nähty {fmtRelative(m.lastSeenAt)}, lisätty{" "}
                  {fmtRelative(m.registeredAt)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDeleteCandidate(m)}
                className="rounded border border-red-800 px-2 py-1 text-xs text-red-300 hover:bg-red-950/40"
                title="Poista kone — daemon voi rekisteröidä itsensä uudelleen jos se on yhä elossa"
              >
                Poista
              </button>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={deleteCandidate !== null}
        title="Poista kone?"
        message={
          <div>
            <p>
              Poistetaan{" "}
              <strong>{deleteCandidate?.label || deleteCandidate?.hostname || "kone"}</strong>{" "}
              koneluettelosta. Cascade-poistaa myös koneen levyraportit ja
              vireillä olevat daemon-pyynnöt.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              Asset:t säilyvät — ne ovat tilin omistuksessa, eivät kone-skooppisia.
              Jos kone on yhä elossa, se rekisteröityy uudelleen seuraavalla
              heartbeat:llä.
            </p>
          </div>
        }
        confirmLabel="Poista"
        destructive
        isProcessing={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteCandidate(null)}
      />
    </section>
  );
}
